import { notification } from "antd";

/**
 * One app-wide notice for connection trouble. Many screens just render an
 * empty table when a request fails, which on a weak connection looks like the
 * data vanished. This tells the user why, without stacking a notice per
 * failed request: every call reuses the same key, so there is only ever one.
 */

const KEY = "tpm-connection-notice";
// Do not re-open a notice of the same kind sooner than this after showing it.
const REPEAT_AFTER_MS = 20000;

const NOTICES = {
  offline: {
    type: "warning",
    message: "You're offline",
    description:
      "New data can't load until your internet connection is back. This message closes by itself when you're online again.",
    duration: 0,
  },
  timeout: {
    type: "warning",
    message: "Slow internet connection",
    description:
      "Some data took too long to load. Check your connection, then refresh the page.",
    duration: 10,
  },
  unreachable: {
    type: "warning",
    message: "Connection problem",
    description:
      "Some data couldn't load because the server can't be reached. Check your internet, then refresh the page.",
    duration: 10,
  },
  session: {
    type: "warning",
    message: "Couldn't renew your session",
    description:
      "Your connection dropped while renewing your sign-in. It will retry automatically — data loads again once the connection is back. You don't need to log out.",
    duration: 12,
  },
};

let current = null;
let shownAt = 0;

// The login page shows its own, more detailed messages.
const onAuthPage = () =>
  /^\/(login|confirmationCode|forgotPassword|sign-up)/i.test(
    window.location.pathname
  );

export const showConnectionNotice = (kind) => {
  const notice = NOTICES[kind];
  if (!notice || onAuthPage()) return;
  if (current === kind && Date.now() - shownAt < REPEAT_AFTER_MS) return;
  current = kind;
  shownAt = Date.now();
  notification[notice.type]({
    key: KEY,
    message: notice.message,
    description: notice.description,
    duration: notice.duration,
    placement: "topRight",
    onClose: () => {
      current = null;
    },
  });
};

let watching = false;

export const watchConnection = () => {
  if (watching || typeof window === "undefined") return;
  watching = true;
  window.addEventListener("offline", () => showConnectionNotice("offline"));
  window.addEventListener("online", () => {
    if (!current) return;
    current = null;
    notification.success({
      key: KEY,
      message: "Back online",
      description: "Your connection is back. Refresh the page if some data is missing.",
      duration: 4,
      placement: "topRight",
    });
  });
};
